/**
 * AddSubmittalModal — create or edit a Submittal (Supabase), scoped to a contract.
 *
 * On create:
 *   - A linked Cortex Thread (Type 'Submittals') is created (owns status/activity/Slack)
 *   - A firm-assigned submittal_number is computed (e.g. "06 10 00-001") and reserved
 *   - This modal captures only the submittal's identity (spec section(s), number, type,
 *     reviewing party). Review rounds (disposition, comments, dates) are logged later via the
 *     review modal, which creates the first revision — no Rev 0 is auto-created here.
 *
 * Spec section picker: common residential CSI sections listed as chips; free-text entry
 * is always available below the chips. The Airtable Specifications table (tblrCqmsGbBCTldzZ)
 * is a project-level document record (not a per-section list), so spec sections are curated
 * here rather than fetched. spec_section_id is stored for future linkage.
 *
 * Multi-section submittals (Submittal Review Plan Phase 1): a GC package often covers several
 * spec sections (e.g. windows + doors from one manufacturer, one data sheet). The picker is a
 * toggle-multi-select — the same pattern already used for Reviewing Party below — and picked
 * sections stack in PICK ORDER (first picked = primary, no drag reorder in v1) as removable
 * chips. The submittal number auto-computes off the primary section only (Decision D3). Edit
 * mode unhides the picker so sections can be added/removed later; the Submittal # field itself
 * becomes read-only once a number has already been assigned (it's the firm-assigned sequence —
 * not meant to be hand-edited after the fact).
 *
 * Opens via CustomEvent('open-add-submittal-modal', { detail }):
 *   create: { contractId, projectId }
 *   edit:   { contractId, projectId, submittalId, prefill: {...} }  // prefill.sections = ordered
 *                                                                    // submittal_spec_sections rows
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { uploadSubmittalFile } from '../services/submittalFiles.js'
import {
  createSubmittal, updateSubmittal,
  getNextSubmittalNumber, submittalLabel,
  SUBMITTAL_STATUS_LABELS, SUBMITTAL_TYPES, SUBMITTAL_REVIEWING_PARTIES,
} from '../services/submittals.js'
import { getProjectSpecSections } from '../services/specSections.js'
import { closeThread, reopenThread } from '../services/threads.js'
import { getProjectPhases } from '../services/phases.js'
import { getMilestoneTemplates, createMilestone } from '../services/milestones.js'
import { seedMilestoneChecklist } from '../services/checklist.js'
import { SUBMITTAL_REVIEW_TEMPLATE_ID } from '../outline/constants.js'
import Select from './Select.jsx'
import { rideSupport } from '../services/passengers.js'

const STATUS_OPTIONS = Object.keys(SUBMITTAL_STATUS_LABELS).map(v => ({ value: v, label: SUBMITTAL_STATUS_LABELS[v] }))

function isoDate(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

/**
 * Create the Submittal's milestone from the 'Submittal Review' deliverable template — same
 * recipe as AddAsiModal's createAsiMilestone (createMilestone + checklist scaffold; no
 * assignment — crews are added on the Flight Board). Returns the new milestone Airtable ID, or
 * null on any failure (non-fatal — the submittal is still created either way).
 */
async function createSubmittalMilestone(projectId, label) {
  const tpls = await getMilestoneTemplates()
  const tpl = (tpls || []).find(t => t.id === SUBMITTAL_REVIEW_TEMPLATE_ID)
  if (!tpl) return null

  let phases = ctx.state?.phases || []
  let csPhase = phases.find(p => ((p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1)
  if (!csPhase) {
    try { csPhase = (await getProjectPhases(projectId)).find(p => ((p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1) }
    catch (_) { /* non-fatal */ }
  }
  const phaseName = csPhase?.fields?.['Phase Name'] || 'Construction Support Phase'

  const today = new Date()
  const lftDate = new Date(today)
  lftDate.setDate(lftDate.getDate() + 7)
  const defDur = Math.max(1, Math.ceil(parseFloat(tpl.fields['Default Days Budgeted'] || 1)))

  const delFields = {
    'Project':               [projectId],
    'Deliverable':           [tpl.id],
    'Project Phase':         phaseName,
    'Latest Finish (LFT)':   isoDate(lftDate),
    'Earliest Start (EST)':  isoDate(today),
    'Duration':              defDur,
    'Created by Automation': true,
    'Create Slack Thread':   true,
    'Subtitle':              ('Submittal — ' + label).trim(),
  }
  if (csPhase) delFields['Phase'] = [csPhase.id]

  const newId = await createMilestone(delFields)

  try {
    await seedMilestoneChecklist(newId)
  } catch (_) { /* non-fatal */ }

  return newId
}

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const roStyle = { ...inputStyle, background: 'var(--aj-bg-muted)', color: 'var(--aj-text-mid)', cursor: 'not-allowed' }
const chip = on => ({ padding: '4px 8px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11, cursor: 'pointer', border: '0.5px solid ' + (on ? '#07272D' : 'var(--aj-border-mid)'), background: on ? '#07272D' : 'var(--aj-bg-elevated)', color: on ? '#fff' : 'var(--aj-text)' })
const specChip = (on, teal) => ({ ...chip(on), ...(teal && on ? { background: '#1a6b5e', borderColor: '#1a6b5e' } : {}) })

export default function AddSubmittalModal() {
  const [open, setOpen]               = useState(false)
  const [contractId, setContractId]   = useState(null)
  const [projectId, setProjectId]     = useState(null)
  const [editId, setEditId]           = useState(null)
  const [submissionId, setSubmissionId] = useState(null)  // contractor-portal intake item being accepted

  // Spec sections — live data from Supabase spec_sections table. Multi-select: ordered array of
  // { id (uuid FK to spec_sections, or null), number, name }. Index 0 = primary section.
  const [sections, setSections]       = useState([])
  const [specFilter, setSpecFilter]   = useState('')
  const [liveSections, setLiveSections] = useState(null)   // null = loading, [] = loaded

  // Submittal fields
  const [submittalNumber, setSubmittalNumber] = useState('')
  const [gcNumber, setGcNumber]       = useState('')
  const [type, setType]               = useState('')
  const [parties, setParties]         = useState([])
  const [status, setStatus]           = useState('open')
  const [dateReceived, setDateReceived] = useState('')   // when the GC submitted the initial package
  const [files, setFiles]             = useState([])   // { file, status: 'pending'|'done'|'error' }
  const fileRef = useRef(null)

  const [submitting, setSubmitting]   = useState(false)
  const [error, setError]             = useState(null)

  // Thread lifecycle prompt
  const [origStatus, setOrigStatus]   = useState('open')
  const [threadAtId, setThreadAtId]   = useState(null)
  const [msAtId, setMsAtId]           = useState(null)
  const [threadPrompt, setThreadPrompt] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  // Auto-compute submittal number when spec section is chosen (create mode only)
  const computeNumber = useCallback(async (pId, sNum) => {
    if (!pId || !sNum) return
    try {
      const n = await getNextSubmittalNumber(pId, sNum)
      if (n) setSubmittalNumber(n)
    } catch (_) { /* non-fatal */ }
  }, [])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      const pId = d.projectId || null
      setContractId(d.contractId || null)
      setProjectId(pId)
      setEditId(d.submittalId || null)
      setSubmissionId(d.submissionId || null)
      const p = d.prefill || {}
      // Seed the multi-section list: prefer the junction-row array (p.sections, ordered by
      // sort_order — edit mode), else fall back to the single flat section (create-from-prefill,
      // or an older record with no junction rows yet).
      if (Array.isArray(p.sections) && p.sections.length) {
        setSections(p.sections.map(s => ({ id: s.spec_section_id || null, number: s.section_number || '', name: s.section_name || '' })))
      } else if (p.spec_section_number) {
        setSections([{ id: p.spec_section_id || null, number: p.spec_section_number, name: p.spec_section_name || '' }])
      } else {
        setSections([])
      }
      setSpecFilter('')
      // Load live spec sections for this project
      if (pId) {
        setLiveSections(null)
        getProjectSpecSections(pId).then(s => setLiveSections(s)).catch(() => setLiveSections([]))
      } else {
        setLiveSections([])
      }
      setSubmittalNumber(p.submittal_number || '')
      setGcNumber(p.gc_number || '')
      setType(p.type || '')
      setParties(Array.isArray(p.reviewing_party) ? p.reviewing_party : [])
      setStatus(p.status || 'open')
      setOrigStatus(p.status || 'open')
      // Initial-submission received date is captured here at creation; default to today on create.
      setDateReceived(p.date_received || (d.submittalId ? '' : new Date().toISOString().slice(0, 10)))
      setThreadAtId(p.thread_airtable_id || null)
      setMsAtId(p.milestone_airtable_id || null)
      setThreadPrompt(null)
      setFiles([])
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-submittal-modal', onOpen)
    return () => window.removeEventListener('open-add-submittal-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const toggle = (val, setter) => setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])

  // Multi-select toggle: clicking an unselected chip appends it (new primary only if the list was
  // empty); clicking an already-selected chip removes it (the next section, if any, becomes the
  // new primary). Mirrors the Reviewing Party toggle pattern above.
  function toggleSpec(sec) {
    // sec may be a live Supabase record { id, section_number, section_name }
    // or a static fallback { number, name }
    const num = sec.section_number || sec.number
    const name = sec.section_name || sec.name
    const id = sec.id || null
    setSections(prev => {
      const idx = prev.findIndex(s => s.number === num)
      const next = idx >= 0 ? prev.filter((_, i) => i !== idx) : [...prev, { id, number: num, name }]
      if (!editId && projectId) {
        if (next.length) computeNumber(projectId, next[0].number)
        else setSubmittalNumber('')
      }
      return next
    })
  }

  function removeSection(idx) {
    setSections(prev => {
      const next = prev.filter((_, i) => i !== idx)
      if (!editId && projectId) {
        if (next.length) computeNumber(projectId, next[0].number)
        else setSubmittalNumber('')
      }
      return next
    })
  }

  function addFiles(e) {
    const picked = Array.from(e.target.files || [])
    setFiles(prev => [...prev, ...picked.map(f => ({ file: f, status: 'pending' }))])
    if (fileRef.current) fileRef.current.value = ''
  }
  function removeFile(idx) { setFiles(prev => prev.filter((_, i) => i !== idx)) }

  // Use live sections from the project's Spec Sections register if available,
  // Firm-curated fallback sections (AJ format: XX-ABC) shown when the project has no
  // spec sections loaded. Mirrors the master spec library — edit to match common submittals.
  const STATIC_CSI = [
    // Metals
    { number: '05-MET', name: 'Metal Fabrications' },
    // Carpentry & Millwork
    { number: '06-FRM', name: 'Rough Carpentry' },
    { number: '06-STR', name: 'Stairs' },
    { number: '06-CSW', name: 'Custom Casework' },
    { number: '06-ITW', name: 'Interior Trim & Moldings' },
    { number: '06-IWW', name: 'Interior Wall Paneling' },
    { number: '06-IWC', name: 'Decorative Wood Ceilings' },
    // Roofing & Waterproofing
    { number: '07-RF',  name: 'Roofing' },
    { number: '07-SWF', name: 'Exterior Siding' },
    { number: '07-DEK', name: 'Exterior Decking' },
    { number: '07-FLS', name: 'Flashing & Sheet Metal' },
    // Doors & Windows
    { number: '08-EXD', name: 'Exterior Doors' },
    { number: '08-WDW', name: 'Windows' },
    { number: '08-IND', name: 'Interior Doors' },
    { number: '08-GRD', name: 'Garage Doors' },
    { number: '08-DWH', name: 'Door Hardware' },
    { number: '08-SHD', name: 'Shower Doors & Glazing' },
    // Finishes
    { number: '09-TIL', name: 'Tile' },
    { number: '09-WDF', name: 'Wood Flooring' },
    { number: '09-CTP', name: 'Countertops' },
    { number: '09-WLC', name: 'Wall Coverings' },
    { number: '09-PAI', name: 'Paints & Coatings' },
    // Specialties
    { number: '10-FPL', name: 'Fireplaces' },
    { number: '10-IG',  name: 'HVAC Grilles & Registers' },
    { number: '10-BTH', name: 'Bath Accessories' },
    // Appliances & Equipment
    { number: '11-REQ', name: 'Appliances' },
    // Plumbing & Mechanical
    { number: '22-PFT', name: 'Plumbing Fixtures' },
    { number: '23-HVAC', name: 'HVAC' },
    // Electrical
    { number: '26-LFX', name: 'Lighting Fixtures' },
    { number: '26-AVD', name: 'AV & Data' },
    { number: '26-ECD', name: 'Electrical Devices & Trim' },
    // Exterior & Site
    { number: '32-PAS', name: 'Stone Paving' },
    { number: '32-PAG', name: 'Gravel Paving' },
    { number: '32-DRV', name: 'Driveway' },
  ]
  const sourceList = Array.isArray(liveSections) && liveSections.length > 0
    ? liveSections.map(s => ({ id: s.id, number: s.section_number, name: s.section_name }))
    : STATIC_CSI
  const filteredSpecs = specFilter.trim()
    ? sourceList.filter(s => (s.number + ' ' + s.name).toLowerCase().includes(specFilter.toLowerCase()))
    : sourceList
  const usingLive = Array.isArray(liveSections) && liveSections.length > 0

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const primary = sections[0] || null
    // Ordered payload for the submittal_spec_sections junction (Phase 1, Decision D1) — the
    // server mirrors index 0 into the flat spec_section_* columns and keeps them in sync.
    const sectionsPayload = sections.map(s => ({ spec_section_id: s.id || null, section_number: s.number, section_name: s.name || null }))
    const fields = {
      gc_number: gcNumber || null,
      type: type || null,
      spec_section_number: primary ? primary.number : null,
      spec_section_name: primary ? primary.name : null,
      spec_section_id: primary ? primary.id : null,
      submittal_number: submittalNumber || null,
      reviewing_party: parties.length ? parties : null,
      date_received: dateReceived || null,
      status,
    }
    try {
      let submittalId = editId
      if (editId) {
        await updateSubmittal(editId, fields, sectionsPayload)
      } else {
        if (!contractId) { setError('No contract in context.'); setSubmitting(false); return }

        // A submittal is a THREAD riding the project's Submittal Support milestone (Matt, 2026-10-24).
        let milestoneId = null, threadId = null
        if (projectId) {
          try {
            const label = submittalLabel({ gc_number: gcNumber }, sectionsPayload)
            const r = await rideSupport(projectId, 'submittal', ('Submittal — ' + label).trim(), '', ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Member Name'])
            threadId = r && r.threadId; milestoneId = r && r.milestoneId
          } catch (_) { /* non-fatal */ }
        }

        const submittal = await createSubmittal(contractId, projectId, { ...fields, thread_airtable_id: threadId }, sectionsPayload)
        submittalId = submittal?.id || null
        // Review rounds (disposition, comments, dates) are logged later via the review modal,
        // which creates the first revision — no Rev 0 is auto-created here.
        // Reload project data so the new milestone appears in the schedule immediately.
        if (milestoneId && projectId && ctx.reloadProjectData) ctx.reloadProjectData(projectId)
      }

      // Upload any attached files as submittal-level attachments (best-effort).
      if (submittalId && files.length) {
        for (let i = 0; i < files.length; i++) {
          try { await uploadSubmittalFile(submittalId, files[i].file); setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'done' } : f)) }
          catch { setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'error' } : f)) }
        }
      }

      if (ctx.submittalCache && contractId) ctx.submittalCache[contractId] = undefined
      // This submittal may ALSO be mounted (now stale) under its milestone or thread — see the
      // identical fix note on AsiNode.applyUpdated in ConstructionSection.jsx.
      if (ctx.milestoneSubmittalCache && msAtId) ctx.milestoneSubmittalCache[msAtId] = undefined
      if (ctx.threadCaItemCache && threadAtId) ctx.threadCaItemCache[threadAtId] = undefined
      window.dispatchEvent(new Event('outline-render'))

      // Status transition + linked thread → offer matching thread activity
      const wasClosed = origStatus === 'closed'
      const nowClosed = status === 'closed'
      if (editId && threadAtId && wasClosed !== nowClosed) {
        setSubmitting(false)
        setThreadPrompt(nowClosed ? 'close' : 'reopen')
        return
      }
      setOpen(false)
    } catch (ex) {
      setError(ex.message || 'Failed to save submittal'); setSubmitting(false)
    }
  }

  async function handleThreadPrompt(confirm) {
    const kind = threadPrompt
    setThreadPrompt(null)
    setOpen(false)
    if (!confirm || !threadAtId) return
    try {
      if (kind === 'close') await closeThread(threadAtId, ctx.currentMemberId || null, 'Submittal marked closed.')
      else await reopenThread(threadAtId, ctx.currentMemberId || null, 'Submittal reopened.')
      const cached = Array.isArray(ctx.threadCache) ? ctx.threadCache.find(t => t.id === threadAtId) : null
      if (cached) { cached.fields['Closed'] = kind === 'close'; cached.fields['Status'] = kind === 'close' ? '⚪ Closed' : '🟢 Open' }
      const caCached = ctx.caThreadCache?.[threadAtId]
      if (caCached?.fields) { caCached.fields['Closed'] = kind === 'close'; caCached.fields['Status'] = kind === 'close' ? '⚪ Closed' : '🟢 Open' }
      if (ctx.threadActCache) ctx.threadActCache[threadAtId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (_) { /* non-fatal */ }
  }

  if (!open) return null

  if (threadPrompt) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div data-modal-id="AddSubmittalModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 400, padding: '20px 20px 16px' }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 8 }}>
            {threadPrompt === 'close' ? 'Close the linked thread?' : 'Reopen the linked thread?'}
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 16 }}>
            {threadPrompt === 'close'
              ? 'The submittal is now closed. Record a "Thread Closed" activity on its linked thread as well?'
              : 'The submittal is open again. Record a "Thread Reopened" activity on its linked thread as well?'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => handleThreadPrompt(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>No, leave it</button>
            <button onClick={() => handleThreadPrompt(true)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              {threadPrompt === 'close' ? 'Close Thread' : 'Reopen Thread'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddSubmittalModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 540, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editId ? 16 : 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{editId ? 'Edit Submittal' : 'Add Submittal'}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        {!editId && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 12 }}>
            A Submittal Review milestone will be created automatically in Construction Support.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── Spec sections (multi-select) ─────────────────────────────────
              Numbers + names come from the SELECTOR only (never free-typed). Picked sections
              stack below in pick order — first picked is the primary (drives the submittal
              number). Click a picked chip again (or its ×) to remove it. */}
          <div>
            <label style={labelStyle}>Spec Section{sections.length > 1 ? 's' : ''}</label>
            <input style={{ ...inputStyle, marginBottom: 6 }} placeholder="Filter sections…"
              value={specFilter} onChange={e => setSpecFilter(e.target.value)} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 120, overflowY: 'auto', padding: '4px 0' }}>
              {liveSections === null && <span style={{ fontSize: 11, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif' }}>Loading…</span>}
              {liveSections !== null && !usingLive && <span style={{ fontSize: 10, color: 'var(--aj-text-mid)', fontFamily: '"DM Mono",monospace', marginBottom: 4, width: '100%' }}>No project spec sections yet — showing common sections</span>}
              {filteredSpecs.map(s => (
                <button key={s.id || s.number} onClick={() => toggleSpec(s)}
                  style={specChip(sections.some(sel => sel.number === s.number), true)}>
                  {s.number} — {s.name}
                </button>
              ))}
              {liveSections !== null && filteredSpecs.length === 0 && (
                <span style={{ fontSize: 11, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif' }}>No match</span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {sections.length === 0 && (
                <span style={{ fontSize: 11, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif' }}>No sections picked yet</span>
              )}
              {sections.map((s, i) => (
                <span key={s.number + '-' + i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 6px 4px 9px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11, background: '#07272D', color: '#fff' }}>
                  {i === 0 && <span title="Primary section" style={{ fontSize: 9 }}>★</span>}
                  {s.number}{s.name ? ' — ' + s.name : ''}
                  <button onClick={() => removeSection(i)} title="Remove"
                    style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          </div>

          {/* ── Identity ─────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: '0 0 140px' }}>
              <label style={labelStyle}>Submittal #</label>
              {editId && submittalNumber ? (
                <input style={roStyle} value={submittalNumber} disabled readOnly />
              ) : (
                <input style={inputStyle} value={submittalNumber} placeholder="06 10 00-001"
                  onChange={e => setSubmittalNumber(e.target.value)} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>GC Submittal #</label>
              <input style={inputStyle} value={gcNumber} onChange={e => setGcNumber(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Type</label>
              <Select value={type} onChange={setType}
                options={[{ value: '', label: '— None —' }].concat(SUBMITTAL_TYPES.map(t => ({ value: t, label: t })))}
                placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>

          {/* ── Status (edit mode) ───────────────────────────────────────── */}
          {editId && (
            <div style={{ width: 160 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          )}

          {/* ── Date received from GC ─────────────────────────────────────
              Recorded here at submittal creation (the GC's initial submission
              date). The review modal shows this value read-only for reference. */}
          <div style={{ width: 200 }}>
            <label style={labelStyle}>Date Received from GC</label>
            <input type="date" style={inputStyle} value={dateReceived} onChange={e => setDateReceived(e.target.value)} />
          </div>

          {/* ── Reviewing party ──────────────────────────────────────────── */}
          <div>
            <label style={labelStyle}>Reviewing Party</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUBMITTAL_REVIEWING_PARTIES.map(p => <button key={p} onClick={() => toggle(p, setParties)} style={chip(parties.includes(p))}>{p}</button>)}
            </div>
          </div>

          {/* ── Attachments — submittal-level files (the submitted package, PDFs) ── */}
          <div>
            <label style={labelStyle}>Attachments</label>
            <div style={{ border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '10px 12px', background: 'var(--aj-bg-subtle)' }}>
              {files.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                      <span style={{ fontSize: 13 }}>📎</span>
                      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                      {f.status === 'done' && <span style={{ fontSize: 11, color: '#1D9E75' }}>✓</span>}
                      {f.status === 'error' && <span style={{ fontSize: 11, color: '#c0392b' }}>✕</span>}
                      {f.status === 'pending' && <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, padding: 0 }}>×</button>}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => fileRef.current?.click()} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add files</button>
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={addFiles} />
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>The submitted package (PDFs) — saved on this submittal.</div>
            </div>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Submittal')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
